import React, { Children, Component } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FAQ as FAQComponent } from '../components/FAQ';
import { Testimonials } from '../components/Testimonials';
import { CTAGroup } from '../components/CTAButtons';
import { GavelIcon, LandmarkIcon, HandshakeIcon, PhoneIcon, MapPinIcon, ArrowRightIcon } from 'lucide-react';
export const FAQ: React.FC = () => {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const staggerChildren = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const otherServices = [{
    title: 'Winding Up Petition Representation',
    description: 'A winding up petition can be very stressful and harmful to your company. Sometimes companies just need time to get their affairs in order without creditors breathing down their neck. We can represent the company in court to get the time you need.',
    icon: GavelIcon
  }, {
    title: 'Unlock Bank Accounts',
    description: 'One of the main issues with a winding up petition is that the bank will often freeze the company bank account, leaving the company unable to trade, pay its staff or its creditors. We can represent you in court so that these accounts can be reopened to allow the company to trade.',
    icon: LandmarkIcon
  }, {
    title: 'Creditor Negotiations',
    description: 'Sometimes it just takes a different approach to get an agreement on an outstanding debt. We have a skilled negotiating team that can help reach a resolution that works for everyone.',
    icon: HandshakeIcon
  }];
  return <div className="bg-white" data-id="element-287">
      {/* Hero Section */}
      <section className="bg-black text-white py-16 md:py-24" data-id="element-288">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-289">
          <motion.div className="max-w-3xl mx-auto text-center" initial="hidden" animate="visible" variants={fadeIn} data-id="element-290">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" data-id="element-291">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-300" data-id="element-292">
              Get clear answers to common questions about selling your company
              and leaving debt behind
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white" data-id="element-293">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-294">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-295">
            <FAQComponent data-id="element-296" />
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-gray-50" data-id="element-297">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-298">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-299">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-300">
              Real Success Stories
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-301">
              Business owners who found their way forward with our help
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-302">
            <Testimonials data-id="element-303" />
          </motion.div>
        </div>
      </section>

      {/* Other Services Section */}
      <section className="py-16 md:py-24 bg-white" data-id="element-304">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-305">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-306">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-307">
              Other Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-308">
              Additional ways we can help your business
            </p>
          </motion.div>
          <motion.div className="grid md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerChildren} data-id="element-309">
            {otherServices.map((service, index) => <motion.div key={index} className="bg-gray-50 p-8 rounded-xl border border-gray-100" variants={fadeIn} data-id="element-310">
                <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-6" data-id="element-311">
                  <service.icon className="h-7 w-7 text-white" data-id="element-312" />
                </div>
                <h3 className="text-xl font-bold mb-4" data-id="element-313">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-314">
                  {service.description}
                </p>
              </motion.div>)}
          </motion.div>
          <motion.div className="text-center mt-12" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-315">
            <Link to="/services" data-id="element-316">
              <button className="inline-flex items-center px-6 py-3 bg-red-700 text-white rounded-md hover:bg-red-800 transition-colors" data-id="element-317">
                <span data-id="element-318">View All Services</span>
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-319" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-16 md:py-24 bg-black text-white" data-id="element-320">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-321">
          <motion.div className="max-w-3xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-322">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-323">
              Let Us Take The Pressure Off
            </h2>
            <p className="text-xl text-gray-300 mb-4" data-id="element-324">
              For a full professional service with ongoing support
            </p>
            <p className="text-lg text-gray-400 mb-8" data-id="element-325">
              So you can dream again and leave your debt problems behind
            </p>

            <div className="bg-gray-900 rounded-xl p-8 mb-8" data-id="element-326">
              <h3 className="text-xl font-bold mb-4 flex items-center justify-center" data-id="element-327">
                <PhoneIcon className="h-5 w-5 mr-2 text-red-500" data-id="element-328" />
                Contact Us
              </h3>
              <p className="text-gray-300 mb-6" data-id="element-329">
                For a free, no obligation chat. We can initially chat by phone
                to see if it's something that might work for you.
              </p>
              <div className="flex items-center justify-center text-gray-400 mb-6" data-id="element-330">
                <MapPinIcon className="h-5 w-5 mr-2 text-red-500" data-id="element-331" />
                <span data-id="element-332">
                  We can do home or site visits at no charge for that personal
                  service
                </span>
              </div>
              <CTAGroup className="justify-center" showPriority={true} data-id="element-333" />
            </div>

            <p className="text-sm text-gray-500" data-id="element-334">
              Average response time: 24 hours | 100% confidential
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
};